/**
 * @module AdapterConfigForm
 * @package @geenius/adapters/solidjs
 * @description Renders the Tailwind-based SolidJS adapter configuration form.
 * It provides a variant-parity counterpart to the React form while preserving
 * Solid primitives and reactivity semantics.
 */

import type { AdapterDomain, DomainAdapterConfig } from "@geenius/adapters";
import { DOMAIN_LABELS, getProvidersForDomain } from "@geenius/adapters";
import { cva } from "class-variance-authority";
import type { JSX } from "solid-js";
import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { cn } from "../lib/utils";

/**
 * Props accepted by the SolidJS adapter configuration form.
 *
 * @property domain Adapter domain being configured in the current dialog
 * @property initialConfig Existing configuration values used to hydrate the form
 * @property onSave Async or sync handler that persists the submitted configuration
 * @property onCancel Optional callback invoked when the user dismisses the form
 * @property class Optional class merged onto the form root
 * @property titleId Optional identifier applied to the form title for dialog labelling
 */
export interface AdapterConfigFormProps {
  domain: AdapterDomain;
  initialConfig?: DomainAdapterConfig;
  onSave: (config: DomainAdapterConfig) => Promise<void> | void;
  onCancel?: () => void;
  class?: string;
  titleId?: string;
}

// Launch DB axis ids (matches React reference). For the db domain we filter
// the provider chooser to localStorage (dev/mock) + these four production
// backends, so library variants don't surface deferred or BYOD-only entries.
const LAUNCH_DB_PROVIDER_IDS = new Set([
  "convex",
  "neon",
  "cloudflareKV",
  "memory",
]);

interface ProviderOptionField {
  key: string;
  label: string;
  description: string;
  placeholder?: string;
  inputMode?: "text" | "numeric";
}

// Provider-specific option fields (parity with React reference). Surfaced as
// extra inputs under the provider chooser; values flow into `config.options`.
const PROVIDER_OPTION_FIELDS: Partial<
  Record<AdapterDomain, Record<string, ProviderOptionField[]>>
> = {
  db: {
    convex: [
      {
        key: "deployment",
        label: "Deployment",
        description: "Convex deployment or project slug.",
        placeholder: "team:project",
      },
    ],
    neon: [
      {
        key: "database",
        label: "Database",
        description: "Database name or branch label for downstream wiring.",
        placeholder: "main",
      },
    ],
    cloudflareKV: [
      {
        key: "namespace",
        label: "KV Namespace",
        description: "Worker binding name used by the downstream KV adapter.",
        placeholder: "ADAPTERS_KV",
      },
      {
        key: "defaultTtlSeconds",
        label: "Default TTL Seconds",
        description: "Optional TTL for cache/session style records.",
        placeholder: "300",
        inputMode: "numeric",
      },
    ],
    memory: [
      {
        key: "seed",
        label: "Fixture Seed",
        description: "Optional deterministic seed for local test data.",
        placeholder: "demo",
      },
    ],
  },
};

function getSelectableProviders(domain: AdapterDomain) {
  const providers = getProvidersForDomain(domain);
  if (domain !== "db") return providers;
  return providers.filter(
    (entry) =>
      entry.id === "localStorage" || LAUNCH_DB_PROVIDER_IDS.has(entry.id),
  );
}

function getOptionFields(
  domain: AdapterDomain,
  provider: string,
): ProviderOptionField[] {
  return PROVIDER_OPTION_FIELDS[domain]?.[provider] ?? [];
}

function stringifyOptionValue(value: unknown): string {
  if (value === undefined || value === null) return "";
  return String(value);
}

function coerceOptionValue(field: ProviderOptionField, value: string): unknown {
  const trimmed = value.trim();
  if (trimmed === "") return "";
  if (field.inputMode !== "numeric") return trimmed;
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : trimmed;
}

function validateOptionValues(
  domain: AdapterDomain,
  provider: string,
  optionValues: Record<string, string>,
): string | null {
  if (domain !== "db" || provider !== "cloudflareKV") return null;
  if (!optionValues.namespace?.trim()) {
    return "KV namespace is required";
  }
  const ttlValue = optionValues.defaultTtlSeconds?.trim();
  if (ttlValue) {
    const ttlSeconds = Number(ttlValue);
    if (!Number.isInteger(ttlSeconds) || ttlSeconds <= 0) {
      return "Default TTL seconds must be a positive integer";
    }
  }
  return null;
}

/**
 * Renders the adapter configuration workflow for a single adapter domain.
 *
 * @param props Component props controlling the selected domain and form callbacks
 * @returns A provider chooser and credential form styled with the SolidJS variant
 * @example
 * ```tsx
 * <AdapterConfigForm
 *   domain="storage"
 *   onSave={async (config) => saveConfig('storage', config)}
 *   onCancel={() => setOpen(false)}
 * />
 * ```
 */
export function AdapterConfigForm(props: AdapterConfigFormProps): JSX.Element {
  const providers = createMemo(() => getSelectableProviders(props.domain));
  const initialProviderId = (): string =>
    providers().find((entry) => entry.id === props.initialConfig?.provider)
      ?.id ??
    providers()[0]?.id ??
    "";

  const [provider, setProvider] = createSignal(initialProviderId());
  const [apiKey, setApiKey] = createSignal(props.initialConfig?.apiKey ?? "");
  const [baseUrl, setBaseUrl] = createSignal(
    props.initialConfig?.baseUrl ?? "",
  );
  const [optionValues, setOptionValues] = createSignal<Record<string, string>>(
    Object.fromEntries(
      Object.entries(props.initialConfig?.options ?? {}).map(([key, value]) => [
        key,
        stringifyOptionValue(value),
      ]),
    ),
  );
  const [isSaving, setIsSaving] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const selectedMeta = () =>
    providers().find((entry) => entry.id === provider());

  const needsApiKey = () => selectedMeta()?.requiresApiKey ?? false;
  const needsBaseUrl = () => selectedMeta()?.requiresBaseUrl ?? false;
  const optionFields = createMemo(() =>
    getOptionFields(props.domain, provider()),
  );

  createEffect(() => {
    const domainKey = props.domain;
    const config = props.initialConfig;
    void domainKey;
    void config;
    const selectableProviders = providers();
    const initialProvider =
      selectableProviders.find((entry) => entry.id === config?.provider)?.id ??
      selectableProviders[0]?.id ??
      "";

    setProvider(initialProvider);
    setApiKey(config?.apiKey ?? "");
    setBaseUrl(config?.baseUrl ?? "");
    setOptionValues(
      Object.fromEntries(
        Object.entries(config?.options ?? {}).map(([key, value]) => [
          key,
          stringifyOptionValue(value),
        ]),
      ),
    );
    setError(null);
  });

  createEffect(() => {
    if (!needsApiKey() && apiKey()) {
      setApiKey("");
    }
    if (!needsBaseUrl() && baseUrl()) {
      setBaseUrl("");
    }
  });

  const handleProviderChange = (nextProvider: string): void => {
    if (nextProvider === provider()) return;
    setProvider(nextProvider);
    setApiKey("");
    setBaseUrl("");
    setOptionValues({});
  };

  const providerOptionVariants = cva(
    "flex items-start gap-4 rounded-lg border p-4 text-left transition-colors motion-reduce:transition-none duration-200",
    {
      variants: {
        selected: {
          true: "border-primary/30 bg-primary/10 shadow-sm",
          false:
            "border-border bg-muted/20 hover:border-primary/20 hover:bg-accent/5",
        },
      },
      defaultVariants: { selected: false },
    },
  );

  const tierVariants = cva(
    "mt-2 inline-flex rounded px-2 py-0.5 text-[0.5rem] font-bold uppercase",
    {
      variants: {
        tier: {
          pronto: "bg-primary/10 text-primary",
          lancio: "bg-accent/10 text-accent",
          studio: "bg-muted text-muted-foreground",
        },
      },
      defaultVariants: { tier: "studio" },
    },
  );

  const handleSubmit = async (event: Event): Promise<void> => {
    event.preventDefault();
    setError(null);

    if (!provider()) {
      setError("Select a provider");
      return;
    }

    if (needsApiKey() && !apiKey().trim()) {
      setError("API key is required");
      return;
    }

    if (needsBaseUrl() && !baseUrl().trim()) {
      setError("Base URL is required");
      return;
    }

    const optionError = validateOptionValues(
      props.domain,
      provider(),
      optionValues(),
    );
    if (optionError) {
      setError(optionError);
      return;
    }

    setIsSaving(true);
    try {
      const config: DomainAdapterConfig = { provider: provider() };
      if (needsApiKey() && apiKey().trim()) {
        config.apiKey = apiKey().trim();
      }
      if (needsBaseUrl() && baseUrl().trim()) {
        config.baseUrl = baseUrl().trim();
      }
      const currentOptions = optionValues();
      const options = Object.fromEntries(
        optionFields()
          .map((field) => [
            field.key,
            coerceOptionValue(field, currentOptions[field.key] ?? ""),
          ])
          .filter(([, value]) => value !== ""),
      );
      if (Object.keys(options).length > 0) {
        config.options = options;
      }
      await props.onSave(config);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      class={cn(
        "space-y-6 rounded-lg border border-border bg-card p-6 shadow-sm",
        props.class,
      )}
      noValidate
    >
      <div>
        <h3 id={props.titleId} class="text-lg font-bold text-foreground">
          Configure {DOMAIN_LABELS[props.domain]}
        </h3>
        <p class="mt-1 text-sm text-muted-foreground">
          Select and configure your preferred provider.
        </p>
      </div>

      <Show when={error()}>
        <div
          class="rounded-md border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          role="alert"
        >
          {error()}
        </div>
      </Show>

      <fieldset class="space-y-3">
        {/* Radio group with fieldset/legend matches the React reference's
            a11y contract for a mutually exclusive choice. */}
        <legend class="block text-xs font-semibold uppercase text-muted-foreground">
          Provider
        </legend>
        <div role="radiogroup" class="grid gap-2 sm:grid-cols-2">
          <For each={providers()}>
            {(providerMeta) => {
              const inputId = `adapter-${props.domain}-provider-${providerMeta.id}`;
              const checked = () => provider() === providerMeta.id;
              return (
                <label
                  for={inputId}
                  class={providerOptionVariants({ selected: checked() })}
                >
                  <input
                    type="radio"
                    id={inputId}
                    name={`adapter-${props.domain}-provider`}
                    value={providerMeta.id}
                    checked={checked()}
                    onChange={() => handleProviderChange(providerMeta.id)}
                    class="sr-only"
                  />
                  <div class="flex-1">
                    <span class="block text-sm font-bold text-foreground">
                      {providerMeta.name}
                    </span>
                    <span class="mt-1 block text-xs leading-relaxed text-muted-foreground">
                      {providerMeta.description}
                    </span>
                    <span class={tierVariants({ tier: providerMeta.tier })}>
                      {providerMeta.tier}
                    </span>
                  </div>
                </label>
              );
            }}
          </For>
        </div>
      </fieldset>

      <Show when={needsApiKey()}>
        <div class="space-y-1.5">
          <label
            class="block text-xs font-semibold uppercase text-muted-foreground"
            for={`adapter-${props.domain}-apiKey`}
          >
            API Key
          </label>
          <input
            id={`adapter-${props.domain}-apiKey`}
            type="password"
            value={apiKey()}
            onInput={(event) => setApiKey(event.currentTarget.value)}
            placeholder={`Enter ${selectedMeta()?.name ?? provider() ?? "provider"} API key`}
            autocomplete="off"
            class="w-full rounded-md border border-input bg-background px-4 py-3 text-sm text-foreground shadow-sm outline-none transition-colors motion-reduce:transition-none placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20"
          />
        </div>
      </Show>

      <Show when={needsBaseUrl()}>
        <div class="space-y-1.5">
          <label
            class="block text-xs font-semibold uppercase text-muted-foreground"
            for={`adapter-${props.domain}-url`}
          >
            Base URL
          </label>
          <input
            id={`adapter-${props.domain}-url`}
            type="url"
            value={baseUrl()}
            onInput={(event) => setBaseUrl(event.currentTarget.value)}
            placeholder="https://..."
            class="w-full rounded-md border border-input bg-background px-4 py-3 text-sm text-foreground shadow-sm outline-none transition-colors motion-reduce:transition-none placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20"
          />
        </div>
      </Show>

      <Show when={optionFields().length > 0}>
        <div class="grid gap-3 sm:grid-cols-2">
          <For each={optionFields()}>
            {(field) => {
              const inputId = `adapter-${props.domain}-${field.key}`;
              const hintId = `${inputId}-hint`;
              return (
                <div class="space-y-1.5">
                  <label
                    for={inputId}
                    class="block text-xs font-semibold uppercase text-muted-foreground"
                  >
                    {field.label}
                  </label>
                  <input
                    id={inputId}
                    type="text"
                    inputMode={field.inputMode}
                    value={optionValues()[field.key] ?? ""}
                    onInput={(event) => {
                      const next = event.currentTarget.value;
                      setOptionValues((current) => ({
                        ...current,
                        [field.key]: next,
                      }));
                    }}
                    placeholder={field.placeholder}
                    aria-describedby={hintId}
                    class="w-full rounded-md border border-input bg-background px-4 py-3 text-sm text-foreground shadow-sm outline-none transition-colors motion-reduce:transition-none placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                  <p id={hintId} class="text-xs text-muted-foreground">
                    {field.description}
                  </p>
                </div>
              );
            }}
          </For>
        </div>
      </Show>

      <div class="flex justify-end gap-3 pt-2">
        <Show when={props.onCancel}>
          <button
            type="button"
            onClick={() => props.onCancel?.()}
            class="rounded-md px-5 py-2.5 text-sm font-semibold text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none hover:text-foreground"
          >
            Cancel
          </button>
        </Show>
        <button
          type="submit"
          disabled={isSaving() || !provider()}
          class="inline-flex items-center justify-center rounded-md border border-primary/30 bg-primary px-8 py-3 text-sm font-bold text-primary-foreground shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
        >
          {isSaving() ? "Saving..." : "Save Configuration"}
        </button>
      </div>
    </form>
  );
}
